<template>
  <section class="v2-field v2-field--flush" data-qid="task-tests">
    <header class="v2-field__head v2-field__head--wrap">
      <h4>{{ $t('testing.title') }}</h4>
      <span class="v2-field__meta">{{ $t('tasks.v2.tests_concluded', { done: concluded, total: tests.length }) }}</span>
      <b-button v-if="canEdit"
                size="sm"
                variant="secondary"
                @click="showTestCreateModal()"
                data-qid="task-tests-new"
      >
        <font-awesome-icon icon="plus" class="mr-1"/>{{ $t('tasks.v2.new_test') }}
      </b-button>
      <b-button v-if="canRemind"
                size="sm"
                variant="secondary"
                @click="showReminderModal"
                data-qid="task-tests-remind"
      >
        <font-awesome-icon icon="bell" class="mr-1"/>{{ $t('tasks.v2.send_reminder') }}
      </b-button>
    </header>
    <div class="v2-progress"><span :style="{ width: percent + '%' }"></span></div>

    <table class="v2-tests" v-if="tests.length">
      <thead>
        <tr>
          <th class="v2-tests__no">#</th>
          <th>{{ $t('tasks.v2.test') }}</th>
          <th>{{ $t('tasks.v2.client') }}</th>
          <th>{{ $t('tasks.v2.result') }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(test, index) in tests"
            :key="'test-' + test.id"
            class="v2-tests__row"
            :class="{'v2-tests__row--failed': test.status === 'failed'}"
            @click="openTestModal(test)"
            data-qid="task-test-row"
        >
          <td class="v2-tests__no">{{ index + 1 }}</td>
          <td>
            <div class="v2-tests__name">{{ test.name }}</div>
            <div class="v2-tests__desc" v-if="test.description">{{ test.description }}</div>
          </td>
          <td class="v2-tests__client">{{ clientSummary(test) }}</td>
          <td><span class="v2-tag" :class="statusOf(test).cls">{{ statusOf(test).text }}</span></td>
          <td class="v2-tests__actions" @click.stop>
            <template v-if="canEdit">
              <span class="v2-field__icon"
                    @click="showEditTestModal(test)"
                    v-b-tooltip.hover
                    :title="$t('system.edit')"
                    data-qid="task-test-edit"
              ><font-awesome-icon icon="pen"/></span>
              <span class="v2-field__icon"
                    :class="{'disabled': duplicating}"
                    @click="duplicateTest(test)"
                    v-b-tooltip.hover
                    :title="$t('tasks.v2.duplicate_test')"
                    data-qid="task-test-duplicate"
              ><font-awesome-icon icon="copy"/></span>
              <span class="v2-field__icon v2-field__icon--danger"
                    @click="showTestConfirmDeleteModal(test)"
                    v-b-tooltip.hover
                    :title="$t('system.delete')"
                    data-qid="task-test-delete"
              ><font-awesome-icon icon="trash-alt"/></span>
            </template>
            <font-awesome-icon icon="chevron-right" class="v2-tests__chevron"/>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-else-if="loadingTests" class="v2-field__empty"><font-awesome-icon icon="spinner" class="fa-spin"/></div>
    <div v-else class="v2-field__empty">{{ $t('tasks.v2.no_tests') }}</div>

    <!-- Reminder to the documentation collection users about the tests waiting on them (US 9452) -->
    <b-modal ref="oe-reminder" :modal-class="v2ModalClass" data-qid="task-tests-remind-modal">
      <template #modal-header>
        <div style="width: 100%;">
          <span @click="hideReminderModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center">{{ $t('tasks.v2.send_reminder') }}</h5>
        </div>
      </template>
      <p>{{ $tc('tasks.v2.reminder_intro', pendingTests, { count: pendingTests }) }}</p>
      <div class="v2-field__meta mb-1">{{ $t('tasks.v2.reminder_to') }}</div>
      <p class="font-weight-bold" data-qid="task-tests-remind-recipients">{{ recipientNames }}</p>
      <b-form-group>
        <label>{{ $t('tasks.v2.reminder_message') }}</label>
        <b-form-textarea v-model="reminderMessage"
                         rows="4"
                         max-rows="8"
                         maxlength="2000"
                         :placeholder="$t('tasks.v2.reminder_message_placeholder')"
                         data-qid="task-tests-remind-message"
        ></b-form-textarea>
      </b-form-group>
      <template #modal-footer>
        <div class="text-right" style="width: 100%;">
          <b-button variant="secondary" @click="hideReminderModal">{{ $t('maintenance.measures.create.cancel') }}</b-button>
          <b-button class="ml-3"
                    variant="success"
                    :disabled="sendingReminder"
                    @click="sendReminder"
                    data-qid="task-tests-remind-send"
          >
            <font-awesome-icon v-if="sendingReminder" icon="spinner" size="sm" class="fa-spin mr-1"/>
            {{ $t('tasks.v2.reminder_send') }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </section>
</template>

<script>
import assessmentSingleTaskProxy from './assessmentSingleTaskProxy';
import piincHttp from '@/util/configureAxios.js';

// Tests (samples) of the OE testing: one row per test, a click opens the test
export default {
  name: 'EngagementTaskTests',
  mixins: [assessmentSingleTaskProxy],
  data() {
    return {
      duplicating: false,
      reminderMessage: '',
      sendingReminder: false
    };
  },
  computed: {
    canEdit() {
      return this.selectedTask && !this.readOnly;
    },
    // Tests the client still has to answer
    pendingTests() {
      return _.filter(this.tests, test => test.status === 'pending').length;
    },
    recipientNames() {
      return _.map(this.selectedTask.data_users || [], 'name').join(', ');
    },
    // Auditors remind the client during OE testing when tests wait on them; the client can't (US 9452)
    canRemind() {
      const user = this.$store.state.user;
      const task = this.selectedTask || {};
      const isDataUser = !!(user && _.some(task.data_users || [], assignee => assignee.id === user.id));
      return this.canEdit && this.isInOeTesting && this.pendingTests > 0 && (task.data_users || []).length > 0
        && (!isDataUser || this.isMainTaskAssignee);
    },
    concluded() {
      return _.filter(this.tests, test => test.status === 'passed' || test.status === 'failed').length;
    },
    percent() {
      return this.tests.length ? Math.round(this.concluded / this.tests.length * 100) : 0;
    }
  },
  methods: {
    showReminderModal() {
      this.reminderMessage = '';
      this.$refs['oe-reminder'].show();
    },
    hideReminderModal() {
      this.$refs['oe-reminder'].hide();
    },
    sendReminder() {
      if (this.sendingReminder) {
        return;
      }
      this.sendingReminder = true;
      piincHttp.post('tasks/' + this.selectedTask.id + '/oe-reminder', { message: this.reminderMessage }).then(response => {
        this.sendingReminder = false;
        this.hideReminderModal();
        this.$toastr('success', this.$tc('tasks.v2.reminder_sent', response.data.recipients, { count: response.data.recipients }));
      }, error => {
        this.sendingReminder = false;
        this.$toastr('error', error.data && error.data.message ? error.data.message : this.$t('tasks.v2.reminder_failed'));
      });
    },
    // A copy with the same description, settings and files, named "<name>-2" etc. by the server
    duplicateTest(test) {
      if (this.duplicating) {
        return;
      }
      this.duplicating = true;
      piincHttp.post('tasks/' + this.selectedTask.id + '/tests/' + test.id + '/duplicate').then(response => {
        this.duplicating = false;
        this.loadTests();
        this.$toastr('success', this.$t('tasks.v2.test_duplicated', { name: response.data.name }));
      }, error => {
        this.duplicating = false;
        this.$toastr('error', error.data && error.data.message ? error.data.message : '');
      });
    },
    statusOf(test) {
      const statuses = {
        pending: { cls: 'v2-tag--grey', text: this.$t('tasks.v2.test_status.pending') },
        documentation_collection_completed: { cls: 'v2-tag--violet', text: this.$t('tasks.v2.test_status.documentation_collection_completed') },
        passed: { cls: 'v2-tag--teal', text: this.$t('tasks.v2.test_status.passed') },
        failed: { cls: 'v2-tag--red', text: this.$t('tasks.v2.test_status.failed') }
      };
      return statuses[test.status] || statuses.pending;
    },
    clientSummary(test) {
      const files = _.filter(test.media || [], { collection_name: 'documents' }).length;
      let parts = [];
      if (files) {
        parts.push(this.$tc('tasks.v2.files_count', files, { count: files }));
      }
      if (test.description_data) {
        parts.push(this.$t('tasks.v2.answered'));
      }
      return parts.length ? parts.join(' · ') : (test.status === 'pending' ? this.$t('tasks.v2.waiting') : '–');
    }
  }
};
</script>
